/**
 * Field-by-field output diff across the compared runs, rendered as an aligned table.
 * Every value is a React text node (never innerHTML) — agent output is untrusted-ish and
 * must not be able to inject markup. Baseline column is neutral; other columns are tinted
 * added / removed / changed vs the baseline.
 */
import { useMemo, useState } from "react";
import { Empty, Segmented } from "antd";
import { diffFields, type DiffStatus } from "../lib/output";
import type { CompareRun } from "../lib/align";

const cellClass: Record<DiffStatus, string> = {
  same: "text-gray-700",
  added: "bg-emerald-50 text-emerald-700",
  removed: "bg-rose-50 text-rose-700 line-through",
  changed: "bg-amber-50 text-amber-800",
};

export const OutputDiff = ({
  runs,
  payloads,
}: {
  runs: CompareRun[];
  payloads: unknown[];
}) => {
  const [mode, setMode] = useState<"changes" | "all">("changes");
  const rows = useMemo(() => diffFields(payloads), [payloads]);
  const changedCount = rows.filter((r) => r.changed).length;
  const visible = mode === "all" ? rows : rows.filter((r) => r.changed);

  if (rows.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No output to compare"
        className="my-6"
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">
          {changedCount === 0
            ? "Outputs are identical across these runs."
            : `${changedCount} field${changedCount === 1 ? "" : "s"} differ from baseline.`}
        </span>
        <Segmented
          size="small"
          value={mode}
          onChange={(v) => setMode(v as "changes" | "all")}
          options={[
            { label: `Changes (${changedCount})`, value: "changes" },
            { label: `All (${rows.length})`, value: "all" },
          ]}
        />
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-1.5 font-semibold text-gray-500 sticky left-0 bg-gray-50">
                Field
              </th>
              {runs.map((r, i) => (
                <th
                  key={r.id}
                  className="text-left px-3 py-1.5 font-semibold min-w-[10rem]"
                >
                  <span className="flex items-center gap-1.5">
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ backgroundColor: r.color }}
                    />
                    <span className="truncate">{r.label}</span>
                    {i === 0 && (
                      <span className="text-[10px] text-gray-400">(base)</span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr
                key={row.path}
                className="border-b border-gray-100 last:border-0 align-top"
              >
                <td className="px-3 py-1.5 font-mono text-gray-500 whitespace-nowrap sticky left-0 bg-white">
                  {row.path}
                </td>
                {row.values.map((val, i) => (
                  <td
                    key={runs[i]?.id ?? i}
                    className={`px-3 py-1.5 font-mono break-all ${cellClass[row.statuses[i]]}`}
                  >
                    {val ?? <span className="text-gray-300">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OutputDiff;
